import useSWR from "swr";
import { useTranslation } from "react-i18next";
import { MapContainer, TileLayer, Circle, Tooltip as LTooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import { fetcher } from "@/lib/api";

const SALNES_CENTER = [42.53, -8.82];

// Sequential emerald→coral scale (lightweight, 6 stops)
const CHOROPLETH_STOPS = [
  "#EDF7F1", // near-zero
  "#B8E4CB",
  "#7ECBA0",
  "#3AB47A",
  "#0F8B54",
  "#0B4C7A", // top emitter
];

function colorFor(value, max) {
  if (!max || value <= 0) return CHOROPLETH_STOPS[0];
  const ratio = Math.max(0, Math.min(1, value / max));
  const idx = Math.min(
    CHOROPLETH_STOPS.length - 1,
    Math.floor(ratio * CHOROPLETH_STOPS.length)
  );
  return CHOROPLETH_STOPS[idx];
}

function fmtInt(n) {
  return new Intl.NumberFormat("es-ES").format(Math.round(n || 0));
}
function fmtDec(n, d = 2) {
  return new Intl.NumberFormat("es-ES", {
    minimumFractionDigits: d,
    maximumFractionDigits: d,
  }).format(n || 0);
}

export default function PegadaConcello({ period }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("es") ? "es" : "gl";
  // Map period → carbon by-concello code
  const periodCode = period === "month" ? "month" : period === "quarter" ? "quarter" : "year";
  const { data } = useSWR(
    `/carbon/by-concello?period=${periodCode}`,
    fetcher,
    { revalidateOnFocus: false }
  );

  const items = data?.items || [];
  const totals = data?.totals || {};
  const factor = data?.factor || {};
  const maxCo2e = items.reduce((m, c) => Math.max(m, c.co2e_kg || 0), 0);

  const chartData = items.map((c) => ({
    name: c.name,
    kg: Math.round(c.kg_total || 0),
    co2e: Math.round(c.co2e_kg || 0),
    per_capita: c.co2e_kg_per_capita,
  }));

  return (
    <div data-testid="pegada-concello">
      {/* Methodology + totals banner */}
      <div
        className="rounded-2xl border hairline bg-salnes-sand/40 p-5 mb-6"
        data-testid="pegada-concello-methodology"
      >
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-salnes-stone mb-1">
              {lang === "es" ? "Metodología" : "Metodoloxía"}
            </div>
            <div className="text-sm text-salnes-ink max-w-2xl">
              {lang === "es"
                ? data?.methodology_es
                : data?.methodology_gl}
            </div>
          </div>
          <div className="text-xs text-salnes-stone whitespace-nowrap">
            <span className="uppercase tracking-[0.18em] text-[10px]">
              {lang === "es" ? "Factor" : "Factor"}
            </span>
            <div className="font-serif text-2xl text-salnes-atlantic tabular-nums">
              {fmtDec(factor.value_kgco2e_per_t, 1)}
              <span className="text-[10px] ml-1 text-salnes-stone uppercase">
                kg CO₂e / t
              </span>
            </div>
            <div className="text-[10px] italic text-salnes-stone mt-0.5">
              {factor.source}
            </div>
          </div>
        </div>
      </div>

      {/* Totals strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Kpi
          label={lang === "es" ? "Residuos recogidos" : "Residuos recollidos"}
          value={fmtDec(totals.t_total || 0, 2)}
          suffix="t"
          testid="pegada-conc-kpi-t"
        />
        <Kpi
          label="CO₂e"
          value={fmtDec(totals.co2e_t || 0, 3)}
          suffix="t"
          accent="text-salnes-coral"
          testid="pegada-conc-kpi-co2e"
        />
        <Kpi
          label={lang === "es" ? "Por habitante" : "Por habitante"}
          value={
            totals.co2e_kg_per_capita != null
              ? fmtDec(totals.co2e_kg_per_capita, 2)
              : "—"
          }
          suffix="kg CO₂e/hab"
          testid="pegada-conc-kpi-percap"
        />
        <Kpi
          label={lang === "es" ? "Eventos siGEUS" : "Eventos siGEUS"}
          value={fmtInt(totals.num_events)}
          testid="pegada-conc-kpi-events"
        />
      </div>

      {/* Choropleth + chart */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Choropleth */}
        <div
          className="rounded-2xl border hairline bg-white p-4"
          data-testid="pegada-concello-choropleth"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] uppercase tracking-[0.22em] text-salnes-stone">
              {lang === "es"
                ? "Emisiones por concello (choropleth)"
                : "Emisións por concello (choropleth)"}
            </div>
            <Legend max={maxCo2e} lang={lang} />
          </div>
          <div className="h-[380px] rounded-xl overflow-hidden" data-testid="pegada-choropleth-map">
            <MapContainer
              center={SALNES_CENTER}
              zoom={10}
              style={{ height: "100%", width: "100%" }}
              scrollWheelZoom={false}
            >
              <TileLayer
                url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                attribution="&copy; OSM · CARTO"
              />
              {items.map((c) => {
                if (!c.center) return null;
                const fill = colorFor(c.co2e_kg || 0, maxCo2e);
                // Radius proportional to co2e (2–6 km visual)
                const rMin = 2500;
                const rMax = 6500;
                const ratio =
                  maxCo2e > 0 ? (c.co2e_kg || 0) / maxCo2e : 0;
                const radius = rMin + ratio * (rMax - rMin);
                return (
                  <Circle
                    key={c.key}
                    center={c.center}
                    radius={radius}
                    pathOptions={{
                      color: fill,
                      weight: 1.5,
                      fillColor: fill,
                      fillOpacity: 0.55,
                    }}
                  >
                    <LTooltip direction="top" opacity={0.95} sticky>
                      <div style={{ font: "12px Inter, sans-serif" }}>
                        <div
                          style={{
                            fontFamily: "'Playfair Display', serif",
                            fontSize: 13,
                            color: "#0B4C7A",
                            fontWeight: 600,
                          }}
                        >
                          {c.name}
                        </div>
                        <div style={{ color: "#374151", marginTop: 4 }}>
                          <b>{fmtDec(c.co2e_kg, 1)}</b> kg CO₂e
                        </div>
                        <div style={{ color: "#6B7280", fontSize: 11 }}>
                          {fmtDec(c.kg_total, 0)} kg · {fmtInt(c.num_events)}{" "}
                          {lang === "es" ? "eventos" : "eventos"}
                        </div>
                        {c.co2e_kg_per_capita != null && (
                          <div
                            style={{
                              color: "#6B7280",
                              fontSize: 11,
                              marginTop: 3,
                            }}
                          >
                            {fmtDec(c.co2e_kg_per_capita, 2)} kg CO₂e/hab
                          </div>
                        )}
                      </div>
                    </LTooltip>
                  </Circle>
                );
              })}
            </MapContainer>
          </div>
        </div>

        {/* Ranking chart */}
        <div
          className="rounded-2xl border hairline bg-white p-4"
          data-testid="pegada-concello-chart"
        >
          <div className="text-[10px] uppercase tracking-[0.22em] text-salnes-stone mb-3">
            {lang === "es"
              ? "Ranking · kg CO₂e por concello"
              : "Ranking · kg CO₂e por concello"}
          </div>
          <div className="h-[380px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                layout="vertical"
                margin={{ left: 10, right: 30, top: 4, bottom: 4 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis
                  type="number"
                  tick={{ fontSize: 11, fill: "#6B7280" }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => fmtInt(v)}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fontSize: 11, fill: "#374151" }}
                  axisLine={false}
                  tickLine={false}
                  width={150}
                />
                <Tooltip
                  contentStyle={{
                    borderRadius: 8,
                    border: "1px solid #E5E7EB",
                    fontSize: 12,
                  }}
                  formatter={(v) => `${fmtInt(v)} kg CO₂e`}
                />
                <Bar dataKey="co2e" fill="#10B981" radius={[0, 6, 6, 0]}>
                  <LabelList
                    dataKey="co2e"
                    position="right"
                    formatter={(v) => fmtInt(v)}
                    style={{ fontSize: 10, fill: "#374151" }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Detail table */}
      <div
        className="rounded-2xl border hairline bg-white overflow-hidden mt-6"
        data-testid="pegada-concello-table"
      >
        <table className="w-full text-sm">
          <thead className="bg-salnes-sand/40 text-salnes-stone">
            <tr>
              <Th>{lang === "es" ? "Concello" : "Concello"}</Th>
              <Th className="text-right">
                {lang === "es" ? "Habitantes" : "Habitantes"}
              </Th>
              <Th className="text-right">kg recogidos</Th>
              <Th className="text-right">t recogidas</Th>
              <Th className="text-right">kg CO₂e</Th>
              <Th className="text-right">kg CO₂e / hab</Th>
              <Th className="text-right">
                {lang === "es" ? "% total" : "% total"}
              </Th>
            </tr>
          </thead>
          <tbody className="divide-y hairline">
            {items.map((c) => (
              <tr
                key={c.key}
                data-testid={`pegada-conc-row-${c.key}`}
              >
                <Td>
                  <span
                    className="inline-block h-2.5 w-2.5 rounded-full mr-2 align-middle"
                    style={{ background: colorFor(c.co2e_kg, maxCo2e) }}
                  />
                  {c.name}
                </Td>
                <Td className="text-right tabular-nums text-salnes-stone">
                  {fmtInt(c.population)}
                </Td>
                <Td className="text-right tabular-nums">
                  {fmtInt(c.kg_total)}
                </Td>
                <Td className="text-right tabular-nums">
                  {fmtDec(c.t_total, 2)}
                </Td>
                <Td className="text-right tabular-nums font-medium text-salnes-atlantic">
                  {fmtInt(c.co2e_kg)}
                </Td>
                <Td className="text-right tabular-nums text-salnes-stone">
                  {c.co2e_kg_per_capita != null
                    ? fmtDec(c.co2e_kg_per_capita, 2)
                    : "—"}
                </Td>
                <Td className="text-right tabular-nums text-salnes-stone">
                  {fmtDec(c.pct_of_total_kg, 1)}%
                </Td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td
                  colSpan="7"
                  className="text-center py-10 text-salnes-stone text-sm"
                >
                  —
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="mt-4 text-[10px] text-salnes-stone italic">
        {lang === "es"
          ? "Fuente: siGEUS · Distromel S.L. (pesajes RFID) + factor MITECO (recogida RSU)."
          : "Fonte: siGEUS · Distromel S.L. (pesaxes RFID) + factor MITECO (recollida RSU)."}
      </p>
    </div>
  );
}

function Kpi({ label, value, suffix, accent = "text-salnes-atlantic", testid }) {
  return (
    <div className="rounded-2xl border hairline bg-white p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.22em] text-salnes-stone">
        {label}
      </div>
      <div className={`mt-1 font-serif text-2xl tabular-nums ${accent}`}>
        {value ?? "—"}
        {suffix && (
          <span className="text-[10px] ml-1 text-salnes-stone uppercase font-sans">
            {suffix}
          </span>
        )}
      </div>
    </div>
  );
}

function Legend({ max, lang }) {
  return (
    <div
      className="flex items-center gap-1.5 text-[10px] text-salnes-stone"
      data-testid="pegada-choropleth-legend"
    >
      <span>0</span>
      {CHOROPLETH_STOPS.map((c, i) => (
        <span
          key={i}
          className="inline-block h-3 w-4"
          style={{ background: c }}
        />
      ))}
      <span className="tabular-nums">
        {fmtInt(max)}{" "}
        <span className="uppercase tracking-wider">
          {lang === "es" ? "kg CO₂e" : "kg CO₂e"}
        </span>
      </span>
    </div>
  );
}

const Th = ({ children, className = "" }) => (
  <th
    className={`text-left text-[10px] uppercase tracking-[0.18em] font-medium px-4 py-3 ${className}`}
  >
    {children}
  </th>
);
const Td = ({ children, className = "" }) => (
  <td className={`px-4 py-3 ${className}`}>{children}</td>
);
